import { Link } from "gatsby"
import moment from "moment"
import React, { useEffect, useState } from "react"
import { FaArrowLeft } from "react-icons/fa"
import { toast } from "react-toastify"

import SetGoalMetrics from "../../pages/goal-setting/set-goal-metrics"
import toastError from "../../utils/toastError"
import {
  AddHabits_InternalLink,
  SetGoalMetrics_InternalLink,
} from "../../utils/urls"
import { DarkAppButton, GreenAppButton } from "../buttons"
import ProceedButton from "../buttons/ProceedButton"
import Badge from "../logos/Badge"
import AddHabitsPage from "../pageComponents/GoalSetting/AddHabitsPage"
import GoalReviewButton from "../pageComponents/GoalSetting/GoalList/GoalReviewButton"
import GoalUpdateButton from "../pageComponents/GoalSetting/GoalList/GoalReviewButton"
import useStore from "../pageComponents/Utils/Store"
import { apiUpdateCheckIn, apiUpdateGoal } from "../providors/goals-api"
import {
  CheckInCompletion,
  iCheckInDict,
  iCheckInValues,
  iGoal,
} from "../types/index"
import { GoalReviewCircleIndicator } from "./GoalReviewCircleIndicator"

type GoalWithCheckIns = iGoal & { checkIns?: iCheckInDict }

export type iCheckinValues = {
  [id: string]: iCheckinUnit
}

export type iCheckinUnit = { [dateStr: string]: CheckInCompletion }

export interface iGoalReviewCard {
  updateDetails: () => void
  location: any
  goalID: string
  // updateSelection: (id: any) => void
  goalWithCheckins: GoalWithCheckIns
  onClickOpenCheckInModal: (
    date: string,
    completed: number,
    goalid: string,
    title: string
  ) => void
}

function checkIfDayHasCheckin(
  date: string,
  goalWithCheckins: GoalWithCheckIns
) {
  if (goalWithCheckins.checkIns) {
    for (const [key] of Object.entries(goalWithCheckins.checkIns)) {
      const keyDate = moment(key).format()
      const dateTime = moment(date).format()
      if (keyDate == dateTime) {
        return true
      }
    }
  }
  return false
}

export const GoalReviewCard: React.FC<iGoalReviewCard> = ({
  goalWithCheckins,
  updateDetails,
  location,
  goalID,
  // updateSelection,
  onClickOpenCheckInModal,
}) => {
  const { reloadTrue } = useStore()

  function checkInDays(goalWithCheckins: GoalWithCheckIns) {
    // This will return an array from with the dates from this Monday to Sunday, and the check in status.

    // TODO: Handle week spanning two months (shouldn't be needed anymore - pls confirm)
    const _values: iCheckinValues = { weekdays: {} }
    const today = moment()

    for (let i = 0; i < 7; i++) {
      // This will get the first day of the current week, starting from a Monday, as ISO has Monday as start of week. It will then add i days.
      const day = moment().startOf("isoWeek").add(i, "d")
      const dayStr = day.format()
      if (day <= today) {
        if (
          goalWithCheckins.checkIns &&
          checkIfDayHasCheckin(dayStr, goalWithCheckins)
        ) {
          const existingValue = goalWithCheckins.checkIns[dayStr].goalCompleted
          _values["weekdays"][dayStr] = existingValue
            ? CheckInCompletion.Completed
            : CheckInCompletion.NotCompleted
        } else {
          _values["weekdays"][dayStr] = CheckInCompletion.Available
        }
      } else {
        _values["weekdays"][dayStr] = CheckInCompletion.NotAvailable
      }
    }
    return _values
  }

  function checkInCompletionBuilder(checkIns: iCheckInValues) {
    // This returns an array of 0-6, which is Monday to Sunday, and the check in status for each day.
    const completionArray = []
    for (var date in checkIns["weekdays"]) {
      completionArray.push({
        date: date,
        checkInStatus: checkIns["weekdays"][date],
      })
    }
    return completionArray
  }

  const checkInDatesAndCompletion = checkInCompletionBuilder(
    checkInDays(goalWithCheckins)
  )
  const goalCompleteDate = (
    dateString: string,
    periodicityString: string
  ): Date => {
    return periodicityString == "day"
      ? moment(dateString).add(goalWithCheckins.forNoPeriods, "d").toDate()
      : periodicityString == "week"
      ? moment(dateString).add(goalWithCheckins.forNoPeriods, "w").toDate()
      : periodicityString == "month"
      ? moment(dateString).add(goalWithCheckins.forNoPeriods, "M").toDate()
      : new Date(dateString)
  }

  // Function to handle the checkbox state change
  const handleGoalCompletion = async () => {
    goalWithCheckins.completed_goal = true
    apiUpdateGoal(goalWithCheckins).catch(error => {
      toastError(toast, "There was an error updating your goal", "top-right")
    })
  }

  function onClickCheckInDayCircle(date: string, completed: number) {
    /* 
        0 is checked in, and completed
        1 is check in, but not completed
        2 is not checked in, and not completed

        This onClick function will call the onClick function in GoalReviewPage, which will pull up the modal with the information.
        
        
        Passing the 1's and 2's through just for extensibility for updating a checkin.
    */

    if (completed == 2) {
      onClickOpenCheckInModal(
        date,
        completed,
        goalWithCheckins.id,
        goalWithCheckins.title
      )
    } else if (completed == 3) {
      onClickOpenCheckInModal(
        date,
        completed,
        goalWithCheckins.id,
        goalWithCheckins.title
      )
      // If we get anything other than 0, we should not be able to change it for now.
    }
  }

  if (!goalWithCheckins.completed_goal)
    return (
      <div className="flex flex-col rounded-md bg-gray-100 border-solid w-full h-auto p-2">
        <p className=" text-lg font-semibold">{goalWithCheckins.title}</p>
        <p className="text-md">
          {goalWithCheckins.noTimesInPeriod +
            " times a " +
            goalWithCheckins.monthPeriod}
        </p>
        <p className="text-sm">
          {new Date(goalWithCheckins.startDate).toLocaleDateString("en-UK") +
            " - " +
            goalCompleteDate(
              goalWithCheckins.startDate,
              goalWithCheckins.monthPeriod
            ).toLocaleDateString("en-UK")}
        </p>
        <div className="pb-2 mt-3 grid grid-cols-7">
          {/* We are passing in the checkIn Dates in order from Monday to Sunday already, so just need to convert them to days of the week in GoalReviewCircleIndicator */}
          {checkInDatesAndCompletion.map((checkIn, index) => (
            <GoalReviewCircleIndicator
              key={index}
              completed={checkIn.checkInStatus}
              date={checkIn.date}
              onClick={onClickCheckInDayCircle}
            />
          ))}
        </div>

        <div className="flex justify-between mt-3 mb-3">
          <Link to={AddHabits_InternalLink} state={{ path: "review", goalID }}>
            <GoalReviewButton enable={true} onClick={updateDetails} />
          </Link>

          <div className="flex">
            <input
              onClick={() => reloadTrue()}
              className="flex items-center justify-center"
              type="checkbox"
              onChange={handleGoalCompletion}
            />
            <label className="flex items-center justify-center px-2 text-xs">
              Set as complete
            </label>
          </div>
        </div>

        {/* <div className="px-8 text-sm flex justify-center items-center">
       
      </div> */}
      </div>
    )

  return (
    <div className="flex justify-between rounded-md bg-gray-100 p-4">
      <div>
        <p className="text-lg font-semibold text-site-blue-dark">
          {goalWithCheckins.title}
        </p>
        <p className="text-md ">
          {goalWithCheckins.noTimesInPeriod +
            " times a " +
            goalWithCheckins.monthPeriod}
        </p>
        <p className="text-sm ">
          {new Date(goalWithCheckins.startDate).toLocaleDateString("en-Uk") +
            " - " +
            goalCompleteDate(
              goalWithCheckins.startDate,
              goalWithCheckins.monthPeriod
            ).toLocaleDateString("en-UK")}
        </p>
      </div>
      <div>
        <Badge width={60} />
      </div>
    </div>
  )
}
